import { Injectable, OnDestroy, inject } from '@angular/core';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ITelemetryPlugin } from '@microsoft/applicationinsights-web';
import { Observable, Subscription, take } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { GlobalErrorHandlerService } from '@core/shared/util';
import { CustomAngularPlugin } from '@mp/shared/app-insights/util';
import { AppConfigService, AppInsightsConfig } from '@mp/shared/data-access';

@Injectable({ providedIn: 'root' })
export class AppInsightsService implements OnDestroy {
  private readonly appConfigService = inject(AppConfigService);
  private readonly customAngularPlugin = inject(CustomAngularPlugin);
  private readonly globalErrorHandler = inject(GlobalErrorHandlerService, { optional: true });

  private _appInsightsInstance?: ApplicationInsights;
  private _appInsightsErrorHandlerInstance?: ApplicationinsightsAngularpluginErrorService;
  private appInsightsSubscription?: Subscription;

  private appInsights$: Observable<ApplicationInsights> = this.appConfigService.appInsightsConfig$.pipe(
    map((appInsightsConfig) => this.createAppInsights(appInsightsConfig)),
    tap(() => {
      // only register the error handler here, do NOT call loadAppInsights() automatically;
      // loading (which creates cookies) must be controlled explicitly via initializeAppInsights()
      this.addAppInsightsErrorHandler();
    }),
    shareReplay(1),
  );

  private localStorageConsent: string | null;

  constructor() {
    this.localStorageConsent = localStorage.getItem('APP_INSIGHTS_CONSENT');
  }

  ngOnDestroy() {
    this.destroyAppInsights();
    this.removeAppInsightsErrorHandler();
  }

  initializeAppInsights(): void {
    // always read latest persisted consent at initialization time
    this.localStorageConsent = localStorage.getItem('APP_INSIGHTS_CONSENT');
    if (this.localStorageConsent === '0') {
      // user explicitly rejected -> ensure destroyed and cookies cleared
      this.destroyAppInsights();
      return;
    }

    // avoid duplicate subscriptions
    if (this.appInsightsSubscription && !this.appInsightsSubscription.closed) {
      return;
    }

    // subscribe and explicitly load the SDK only when consent allows it
    this.appInsightsSubscription = this.appInsights$.pipe(take(1)).subscribe((appInsights) => {
      // store instance (createAppInsights already sets _appInsightsInstance)
      this._appInsightsInstance = appInsights;

      // loadAppInsights actually injects SDK code that creates cookies — only call when allowed
      this.loadAppInsights(appInsights);
    });
  }

  trackEvent(name: string, customProperties?: { [key: string]: unknown }): void {
    this.dispatchInsightsAction((appInsights) => appInsights.trackEvent({ name }, customProperties));
  }

  destroyAppInsights(): void {
    // unsubscribe from the observable to avoid leaking subscriptions and to stop reloads
    this.appInsightsSubscription?.unsubscribe();
    this.appInsightsSubscription = undefined;

    if (!this._appInsightsInstance) {
      // still clear any existing ai_* cookies to be sure
      this.clearAppInsightsCookies();
      return;
    }

    // disable telemetry so SDK won't send anything
    if (this._appInsightsInstance.config) {
      this._appInsightsInstance.config.disableTelemetry = true;
    }

    this._appInsightsInstance.unload();

    this._appInsightsInstance = undefined;

    // remove cookies after unloading
    this.clearAppInsightsCookies();
  }

  private createAppInsights(insightsConfig: AppInsightsConfig): ApplicationInsights {
    this.customAngularPlugin.initializePlugin();

    this._appInsightsInstance = new ApplicationInsights({
      config: {
        connectionString: insightsConfig.connectionString,
        autoTrackPageVisitTime: true,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        extensions: [this.customAngularPlugin as unknown as ITelemetryPlugin],
        extensionConfig: {
          [this.customAngularPlugin.identifier]: {
            router: this.customAngularPlugin.router,
          },
        },
      },
    });

    return this._appInsightsInstance;
  }

  private clearAppInsightsCookies(): void {
    try {
      const cookies = document.cookie ? document.cookie.split(';').map((c) => c.trim()) : [];
      const aiCookieNames = cookies
        .map((c) => c.split('=')[0])
        .filter((name) => /^ai_/.test(name) || name === 'ai_user' || name === 'ai_session');

      const domains = ['', `.${location.hostname}`];
      const paths = ['/', ''];

      aiCookieNames.forEach((name) => {
        domains.forEach((domain) => {
          paths.forEach((path) => {
            // set expired cookie for each domain/path combination
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};${domain ? `domain=${domain};` : ''}`;
          });
        });
      });
    } catch (e) {
      // swallow errors - clearing cookies is best-effort
      console.warn('Failed to clear AppInsights cookies', e);
    }
  }

  private loadAppInsights(appInsights: ApplicationInsights): void {
    appInsights.loadAppInsights();
  }

  private dispatchInsightsAction(action: AppInsightsAction): void {
    this.appInsights$.subscribe((appInsights) => action(appInsights));
  }

  private addAppInsightsErrorHandler() {
    if (this.globalErrorHandler) {
      this._appInsightsErrorHandlerInstance = new ApplicationinsightsAngularpluginErrorService();
      this.globalErrorHandler.addErrorHandler(this._appInsightsErrorHandlerInstance);
    }
  }

  private removeAppInsightsErrorHandler() {
    if (!this._appInsightsErrorHandlerInstance) {
      return;
    }

    this.globalErrorHandler?.removeErrorHandler(this._appInsightsErrorHandlerInstance);

    this._appInsightsErrorHandlerInstance = undefined;
  }
}

type AppInsightsAction = (appInsights: ApplicationInsights) => void;
